import { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight, Loader2, Sparkles, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { errMsg } from "../lib/errors";

/**
 * <CBCTViewer> — modal slice slider for a single CBCT series.
 *
 * Props:
 *   study             from GET /patients/{pid}/cbct-studies (single entry)
 *   patientId         passed through for the "extract key slices" endpoint
 *   onClose()         user closed the modal
 *   onKeySlicesAdded(files) — fired after a successful extract; lets the
 *                            parent refresh its file list so the newly-derived
 *                            radiograph_2d slices appear in the gallery
 */
export default function CBCTViewer({ study, patientId, onClose, onKeySlicesAdded }) {
  const [idx, setIdx] = useState(0);
  const [pngUrl, setPngUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const slices = useMemo(() => study?.slice_file_ids || [], [study]);
  const n = slices.length;

  // Fetch the current slice PNG (authenticated). Browser keeps recently fetched
  // blobs in the http cache for instant re-scrubbing.
  useEffect(() => {
    if (!n || idx < 0 || idx >= n) return;
    let cancelled = false;
    let activeBlobUrl = null;
    setLoading(true);
    const token = localStorage.getItem("pa_token");
    fetch(`${API}/cbct-studies/render/${slices[idx]}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((blob) => {
        if (cancelled) return;
        activeBlobUrl = URL.createObjectURL(blob);
        setPngUrl(activeBlobUrl);
      })
      .catch((err) => {
        if (!cancelled) toast.error(`Slice render failed: ${err.message}`);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      if (activeBlobUrl) URL.revokeObjectURL(activeBlobUrl);
    };
  }, [idx, slices, n]);

  // Keyboard nav: ←/→, Home/End, Esc
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft" || e.key === "ArrowDown") setIdx((i) => Math.max(0, i - 1));
      else if (e.key === "ArrowRight" || e.key === "ArrowUp") setIdx((i) => Math.min(n - 1, i + 1));
      else if (e.key === "Home") setIdx(0);
      else if (e.key === "End") setIdx(n - 1);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [n, onClose]);

  const extractKey = async () => {
    setExtracting(true);
    try {
      const r = await api.post(
        `/cbct-studies/${patientId}/${encodeURIComponent(study.series_uid)}/extract-key-slices`
      );
      toast.success(`Extracted ${r.data.extracted_count} key slice(s) for Claude AI`);
      onKeySlicesAdded?.(r.data.files);
    } catch (err) {
      toast.error(errMsg(err, "Key-slice extraction failed"));
    } finally {
      setExtracting(false);
    }
  };

  const px = study.pixel_spacing_row_mm
    ? `${study.pixel_spacing_row_mm.toFixed(2)} × ${
        (study.pixel_spacing_col_mm || study.pixel_spacing_row_mm).toFixed(2)
      } mm/px`
    : null;

  return (
    <div
      data-testid="cbct-viewer"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl bg-[var(--pa-surface)] border border-[var(--pa-gold)]/40 rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[var(--pa-border)]">
          <div>
            <div className="kicker">CBCT Volume · {study.modality || "—"}</div>
            <h2
              data-testid="cbct-viewer-title"
              className="font-serif text-xl text-[var(--pa-text)] mt-1"
            >
              {study.series_description || study.study_description || "Untitled series"}
            </h2>
            <div className="text-[10px] text-[var(--pa-text-dim)] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              {study.body_part_examined && <span>· {study.body_part_examined}</span>}
              {study.study_date && <span>· {study.study_date}</span>}
              {study.rows && study.columns && (
                <span>
                  · {study.columns}×{study.rows} px
                </span>
              )}
              {px && <span>· {px}</span>}
              {study.slice_thickness_mm && (
                <span>· {study.slice_thickness_mm.toFixed(2)} mm slice</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="cbct-viewer-close"
            className="p-2 rounded text-[var(--pa-text-dim)] hover:text-[var(--pa-text)]"
            title="Close (Esc)"
          >
            <X size={16} />
          </button>
        </div>

        {/* Image stage */}
        <div className="relative bg-black flex items-center justify-center min-h-[420px]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-[var(--pa-text-dim)]">
              <Loader2 size={16} className="animate-spin text-[var(--pa-gold)]" />
            </div>
          )}
          {pngUrl && (
            <img
              src={pngUrl}
              alt={`Slice ${idx + 1} of ${n}`}
              data-testid="cbct-viewer-slice"
              className="max-h-[520px] max-w-full object-contain transition-opacity duration-100"
              style={{ opacity: loading ? 0.3 : 1 }}
            />
          )}
          {/* Prev/next overlay arrows */}
          <button
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            data-testid="cbct-viewer-prev"
            className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition disabled:opacity-30"
            title="Previous slice (←)"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => setIdx((i) => Math.min(n - 1, i + 1))}
            disabled={idx === n - 1}
            data-testid="cbct-viewer-next"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition disabled:opacity-30"
            title="Next slice (→)"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Slider + actions */}
        <div className="p-5 border-t border-[var(--pa-border)] space-y-3">
          <div className="flex items-center gap-3 text-xs text-[var(--pa-text-muted)]">
            <Layers size={12} className="text-[var(--pa-gold)]" />
            <span className="tabular-nums">
              Slice <span className="text-[var(--pa-text)] font-semibold">{idx + 1}</span> /{" "}
              {n}
            </span>
            <input
              type="range"
              min={0}
              max={Math.max(0, n - 1)}
              value={idx}
              onChange={(e) => setIdx(parseInt(e.target.value, 10))}
              data-testid="cbct-viewer-slider"
              className="flex-1 accent-[var(--pa-gold)]"
            />
            <span className="text-[10px] text-[var(--pa-text-dim)]">← / →</span>
          </div>

          <button
            type="button"
            onClick={extractKey}
            disabled={extracting}
            data-testid="cbct-viewer-extract-key"
            className="w-full flex items-center justify-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-4 py-2.5 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-60"
          >
            {extracting ? (
              <>
                <Loader2 size={13} className="animate-spin" /> Extracting key slices…
              </>
            ) : (
              <>
                <Sparkles size={13} /> Extract 3 key slices for Claude AI
              </>
            )}
          </button>
          <p className="text-[10px] text-[var(--pa-text-dim)] leading-relaxed text-center">
            Pulls slices at 25% / 50% / 75% of the series depth as PNGs that flow
            straight into the Claude vision pipeline for any case analysis on this patient.
          </p>
        </div>
      </div>
    </div>
  );
}
